import React, { useState, useEffect } from 'react';
import { ReactFormGenerator } from 'react-form-builder2';
import { PlusIcon, TrashIcon, XMarkIcon, MagnifyingGlassIcon, CheckIcon, DocumentIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import toast from '../utils/toast';
import PasteUploadArea from './PasteUploadArea';
import { createVerificationRecord } from '../utils/documentVerification';
import { useFeeSettings } from '../utils/feeSettings';

const FormSubmission = ({ 
  form: initialForm, 
  onSubmit, 
  onCancel, 
  isPublic = false, 
  apiCall = null, 
  token = null 
}) => {
  const { showFees, loading: feeSettingsLoading } = useFeeSettings();
  
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submissionType, setSubmissionType] = useState(''); // 'task' or 'form_submission'
  const [productsData, setProductsData] = useState({});
  const [showProductModal, setShowProductModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [selectedMasterData, setSelectedMasterData] = useState(null);

  // Master data selection states
  const [masterDataList, setMasterDataList] = useState([]);
  const [masterDataLoading, setMasterDataLoading] = useState(false);
  const [masterDataSearchTerm, setMasterDataSearchTerm] = useState('');
  const [clientTypes, setClientTypes] = useState([]);
  const [selectedClientType, setSelectedClientType] = useState('');
  const [showMasterDataDropdown, setShowMasterDataDropdown] = useState(false);

  useEffect(() => {
    if (initialForm) {
      // Set the form with all its associated products
      setForm(initialForm);
    }
  }, [initialForm]);

  useEffect(() => {
    if (form) {
      initializeForm();
      fetchMasterData();
      fetchClientTypes();
      fetchAvailableProducts();
    }
  }, [form]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMasterDataDropdown && !event.target.closest('.master-data-dropdown')) {
        setShowMasterDataDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMasterDataDropdown]);

  const initializeForm = () => {
    if (!form) return;

    // Initialize products data
    let initialProductsData = {};
    console.log("form.associatedProducts", form.associatedProducts);
    // Automatically add products that are associated with the form
    if (form.associatedProducts && form.associatedProducts.length > 0) {
      form.associatedProducts.forEach(({ productId }, index) => {
        const productData = {
          id: productId._id,
          productIndex: index,
          instance: 0, // Add the missing instance property
          "Government Fees": productId.fees.governmentFees.amount,
          "Professional Fees": productId.fees.professionalFees.amount,
          "Other Fees": {}
        };
        // Add other fees
        productId.fees.otherFees.forEach(fee => {
          productData["Other Fees"][fee.name] = fee.amount;
        });
        // Add required fields
        productId.requiredFields.forEach(field => {
          productData[field.label] = '';
        });
        initialProductsData[`${index}_0`] = productData;
      });
    }
    console.log(initialProductsData);
    setProductsData(initialProductsData);
  };

  const fetchMasterData = async () => {
    try {
      setMasterDataLoading(true);
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await apiCall.get('/master-data', { headers });
      setMasterDataList(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    } finally {
      setMasterDataLoading(false);
    }
  };

  const fetchClientTypes = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await apiCall.get('/admin/client-types', { headers });
      setClientTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch client types:', error);
    }
  };

  const fetchAvailableProducts = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await apiCall.get('/admin/products', { headers });
      setAvailableProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch available products:', error);
    }
  };

  // Get products that are not already added to the form
  const getAvailableProductsForSelection = () => {
    // Get IDs of products already added to the form
    const addedProductIds = Object.values(productsData).map(data => data.id);
    
    // If there are associated products, return only those that haven't been added yet
    if (form.associatedProducts && form.associatedProducts.length > 0) {
      return form.associatedProducts
        .filter(({ productId }) => !addedProductIds.includes(productId._id))
        .map(({ productId }) => productId);
    }
    
    // If no associated products, return all available products that haven't been added yet
    if (availableProducts && availableProducts.length > 0) {
      return availableProducts.filter(product => !addedProductIds.includes(product._id));
    }
    
    return [];
  };

  // Add function to remove a specific instance
  const handleRemoveInstance = (productIndex, instanceKey) => {
    setProductsData(prev => {
      const updated = { ...prev };
      delete updated[instanceKey];
      return updated;
    });
  };

  const filteredMasterData = masterDataList.filter(item => {
    const matchesSearch = item.clientName?.toLowerCase().includes(masterDataSearchTerm.toLowerCase()) ||
      item.clientAddress?.toLowerCase().includes(masterDataSearchTerm.toLowerCase()) ||
      item.contactPersons?.some(contact =>
        contact.name?.toLowerCase().includes(masterDataSearchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(masterDataSearchTerm.toLowerCase())
      );

    const matchesClientType = !selectedClientType || item.clientType === selectedClientType;

    return matchesSearch && matchesClientType;
  });

  const handleFileUpload = async (productIndex, fieldName, file) => {
    try {
      setUploadingFiles(prev => ({
        ...prev,
        [`${productIndex}_${fieldName}`]: true
      }));

      const formData = new FormData();
      formData.append('file', file);

      const headers = token ? { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      } : { 'Content-Type': 'multipart/form-data' };

      const response = await apiCall.post('/upload', formData, { headers });

      // Store the file URL in productsData
      setProductsData(prev => {
        const updated = { ...prev };
        updated[`${productIndex}_0`] = {
          ...updated[`${productIndex}_0`],
          [fieldName]: response.data.url // Store the URL instead of the file
        };
        return updated;
      });

      // Create verification record for the uploaded document
      try {
        const verificationData = {
          documentType: 'formSubmission',
          documentId: form._id, // Form ID
          documentField: fieldName,
          fileUrl: response.data.url,
          fileName: file.name,
          fileSize: file.size
        };

        const verificationResult = await createVerificationRecord(verificationData);
        
        if (verificationResult.verificationRequired) {
          toast.info('Document uploaded successfully. Verification required before approval.');
        } else {
          toast.success('File uploaded successfully');
        }
      } catch (verificationError) {
        console.error('Verification error:', verificationError);
        toast.success('File uploaded successfully');
      }

    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFiles(prev => {
        const updated = { ...prev };
        delete updated[`${productIndex}_${fieldName}`];
        return updated;
      });
    }
  };

  const handleFieldChange = (productIndex, fieldName, value) => {
    // If it's a file input
    if (value instanceof File) {
      handleFileUpload(productIndex, fieldName, value);
      return;
    }

    // Handle other field types as before
    setProductsData(prev => {
      const updated = { ...prev };
      updated[`${productIndex}_0`] = {
        ...updated[`${productIndex}_0`],
        [fieldName]: value
      };
      return updated;
    });
  };

  const handleFeeChange = (productKey, feeType, feeName, value) => {
    setProductsData(prev => {
      const updatedData = { ...prev };
      if (feeType === 'Other Fees') {
        updatedData[productKey] = {
          ...updatedData[productKey],
          [feeType]: {
            ...updatedData[productKey][feeType],
            [feeName]: value
          }
        };
      } else {
        updatedData[productKey] = {
          ...updatedData[productKey],
          [feeType]: value
        };
      }
      return updatedData;
    });
  };

  const calculateTotalFees = (productKey) => {
    const productData = productsData[productKey];
    if (!productData) return 0;

    let total = 0;
    total += parseFloat(productData['Government Fees'] || 0);
    total += parseFloat(productData['Professional Fees'] || 0);

    if (productData['Other Fees']) {
      Object.values(productData['Other Fees']).forEach(fee => {
        total += parseFloat(fee || 0);
      });
    }

    return total.toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if master data is selected
    if (!selectedMasterData) {
      setMessage({
        type: 'error',
        text: 'Please select a client before submitting the form.'
      });
      setShowMessage(true);
      return;
    }

    // Now proceed with form submission
    await submitFormWithMasterData(selectedMasterData);
  };

  const handleMasterDataSelect = (masterData) => {
    setSelectedMasterData(masterData);
    setShowMasterDataDropdown(false);
  };

  const submitFormWithMasterData = async (masterData) => {
    // Validate if form is filled
    // if (!formData || Object.keys(formData).length === 0) {
    //   setMessage({
    //     type: 'error',
    //     text: 'Please fill in the form before submitting.'
    //   });
    //   setShowMessage(true);
    //   return;
    // }

    // Validate required product fields
    let hasEmptyRequiredFields = false;
    form.associatedProducts.forEach(({ productId }, productIndex) => {
      // Get all instances for this product
      const productInstances = Object.entries(productsData)
        .filter(([key]) => key.startsWith(`${productIndex}_`))
        .map(([key, data]) => ({ key, data }));

      productInstances.forEach(({ data }) => {
        productId.requiredFields.forEach(field => {
          if (field.isRequired && !data[field.label]) {
            hasEmptyRequiredFields = true;
          }
        });
      });
    });

    if (hasEmptyRequiredFields) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required product fields.'
      });
      setShowMessage(true);
      return;
    }

    try {
      setSubmitting(true);

      // Transform productsData into a comprehensive format
      const transformedProductsData = Object.entries(productsData).map(([key, data]) => {
        const [productIndex, instance] = key.split('_').map(Number);
        const product = form.associatedProducts[productIndex].productId;

        // Ensure fee amounts have default values
        const governmentFees = parseFloat(data['Government Fees'] || 0);
        const professionalFees = parseFloat(data['Professional Fees'] || 0);
        const otherFees = Object.entries(data['Other Fees'] || {}).map(([name, amount]) => ({
          name,
          amount: parseFloat(amount || 0),
          isCustomizable: product.fees.otherFees.find(f => f.name === name)?.isCustomizable || false
        }));

        // Calculate total fees with safe values
        const totalFees = [
          governmentFees,
          professionalFees,
          ...otherFees.map(fee => fee.amount)
        ].reduce((sum, fee) => sum + (fee || 0), 0);

        return {
          id: data.id,
          productIndex,
          instance,
          name: product.name,
          description: product.description,
          fees: {
            governmentFees: {
              name: product.fees.governmentFees.name,
              amount: governmentFees,
              isCustomizable: product.fees.governmentFees.isCustomizable
            },
            professionalFees: {
              name: product.fees.professionalFees.name,
              amount: professionalFees,
              isCustomizable: product.fees.professionalFees.isCustomizable
            },
            otherFees
          },
          documents: data.documents || [],
          verifications: data.verifications || [],
          requiredFields: product.requiredFields.map(field => ({
            label: field.label,
            fieldType: field.fieldType,
            isRequired: field.isRequired,
            value: data[field.label] || null,
            options: field.options
          })),
          totalFees,
          status: 'pending'
        };
      });

      // Calculate total fees for all products
      const totalFees = transformedProductsData.reduce((sum, product) => sum + product.totalFees, 0);

      // Create comprehensive submission data
      const submissionPayload = {
        formId: form._id,
        masterDataId: masterData._id,
        formName: form.name,
        formDescription: form.description,
        submissionData: {
          formFields: Object.entries(formData).map(([name, value]) => ({
            name,
            value,
            field: form.schema.task_data.find(f => f.field_name === name)
          })),
          submittedAt: new Date().toISOString(),
          lastUpdatedAt: new Date().toISOString(),
          status: 'pending',
          totalProducts: transformedProductsData.length,
          totalFees,
          products: transformedProductsData
        },
        historyEntry: {
          actionType: 'STATUS_CHANGED',
          details: {
            totalProducts: transformedProductsData.length,
            totalFees,
            oldValue: null,
            newValue: 'pending'
          },
          metadata: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        }
      };

      // Call the onSubmit callback with the payload
      const result = await onSubmit(submissionPayload);

      // Handle the result
      if (result && result.success) {
        setMessage({
          type: 'success',
          text: result.message || 'Form submitted successfully!'
        });
        setShowMessage(true);
        setSubmitted(true);
        setSubmissionType(result.type || 'form_submission');
        setFormData({});
        setProductsData({});
      }

    } catch (error) {
      console.error('Submission error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to submit form. Please try again.'
      });
      setShowMessage(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
    setShowMessage(false);
    setSubmissionType('');
    setFormData({});
    setProductsData({});
    setSelectedMasterData(null);
    initializeForm();
  };

  const handleAddProduct = () => {
    setShowProductModal(true);
  };

  const handleProductSelect = (product) => {
    // Add the selected product to form.associatedProducts
    const newProductIndex = form.associatedProducts.length;

    setForm(prevForm => ({
      ...prevForm,
      associatedProducts: [
        ...prevForm.associatedProducts,
        {
          productId: product,
          _id: Date.now() // temporary ID for new products
        }
      ]
    }));

    // Initialize the first instance of the product
    setProductsData(prevData => ({
      ...prevData,
      [`${newProductIndex}_0`]: {
        id: product._id,
        name: product.name,
        instance: 0,
        'Government Fees': product.fees.governmentFees.amount,
        'Professional Fees': product.fees.professionalFees.amount,
        'Other Fees': product.fees.otherFees.reduce((acc, fee) => {
          acc[fee.name] = fee.amount;
          return acc;
        }, {}),
        // Initialize required fields
        ...product.requiredFields.reduce((acc, field) => {
          acc[field.label] = '';
          return acc;
        }, {})
      }
    }));

    setShowProductModal(false);
    setSelectedProduct(null);
  };

  const handleRemoveProduct = (productIndex) => {
    // Remove the product from form.associatedProducts
    setForm(prevForm => ({
      ...prevForm,
      associatedProducts: prevForm.associatedProducts.filter((_, index) => index !== productIndex)
    }));

    // Remove all instances of this product from productsData
    setProductsData(prevData => {
      const newData = { ...prevData };
      Object.keys(newData).forEach(key => {
        if (key.startsWith(`${productIndex}_`)) {
          delete newData[key];
        }
      });
      return newData;
    });
  };

  // Add another instance of a product
  const handleAddProductInstance = (productIndex) => {
    const product = form.associatedProducts[productIndex].productId;
    setProductsData(prevData => {
      // Find the highest instance number for this product
      const instanceNumbers = Object.keys(prevData)
        .filter(key => key.startsWith(`${productIndex}_`))
        .map(key => parseInt(key.split('_')[1]));
      const nextInstance = instanceNumbers.length > 0 ? Math.max(...instanceNumbers) + 1 : 1;
      
      // Initialize with default values for new instance
      const newInstanceData = {
        id: product._id,
        name: product.name,
        instance: nextInstance,
        'Government Fees': product.fees.governmentFees.amount,
        'Professional Fees': product.fees.professionalFees.amount,
        'Other Fees': product.fees.otherFees.reduce((acc, fee) => {
          acc[fee.name] = fee.amount;
          return acc;
        }, {}),
        // Initialize required fields
        ...product.requiredFields.reduce((acc, field) => {
          acc[field.label] = '';
          return acc;
        }, {})
      };
      
      return {
        ...prevData,
        [`${productIndex}_${nextInstance}`]: newInstanceData
      };
    });
  };

  const renderProductFees = () => {
    // Group products by their base index (productIndex)
    const productGroups = {};
    
    Object.entries(productsData).forEach(([key, data]) => {
      const [productIndex] = key.split('_').map(Number);
      if (!productGroups[productIndex]) {
        productGroups[productIndex] = [];
      }
      productGroups[productIndex].push({ key, data });
    });

    return Object.entries(productGroups).map(([productIndex, instances]) => {
      // Get the first instance to get product info
      const firstInstance = instances[0];
      const productData = firstInstance.data;
      
      // Find the product info from availableProducts or form.associatedProducts
      let productInfo = null;
      
      // First try to find in availableProducts
      if (availableProducts) {
        productInfo = availableProducts.find(p => p._id === productData.id);
      }
      
      // If not found, try form.associatedProducts
      if (!productInfo && form.associatedProducts) {
        const associatedProduct = form.associatedProducts.find((_, index) => index === parseInt(productIndex));
        if (associatedProduct) {
          productInfo = associatedProduct.productId;
        }
      }
      
      // If still not found, use basic info from productData
      if (!productInfo) {
        productInfo = {
          name: productData.name || `Product ${parseInt(productIndex) + 1}`,
          fees: {
            governmentFees: { name: 'Government Fees', isCustomizable: true },
            professionalFees: { name: 'Professional Fees', isCustomizable: true },
            otherFees: []
          },
          requiredFields: []
        };
      }

      return (
        <div key={`product_group_${productIndex}`} className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">
              {productInfo.name}
            </h3>
            {/* <button
              type="button"
              onClick={() => handleRemoveProduct(parseInt(productIndex))}
              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-red-600 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              Remove Product
            </button> */}
          </div>

          {instances.map(({ key, data }) => (
            <div key={key} className="mb-8 bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-gray-800">
                  Instance #{data.instance + 1}
                </h4>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => handleAddProductInstance(parseInt(productIndex))}
                    className="inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Instance
                  </button>
                  {instances.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveInstance(parseInt(productIndex), key)}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded-md text-red-600 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Remove Instance
                    </button>
                  )}
                </div>
              </div>

              {/* Government Fees */}
              {showFees && !feeSettingsLoading && (
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <label className="text-md font-medium text-gray-700">
                      {productInfo.fees?.governmentFees?.name || 'Government Fees'}
                    </label>
                    <div className="w-1/3">
                      <input
                        type="number"
                        value={data['Government Fees'] || 0}
                        onChange={(e) => handleFeeChange(
                          key,
                          'Government Fees',
                          null,
                          parseFloat(e.target.value) || 0
                        )}
                        className="w-full px-3 py-2 border rounded-md"
                        disabled={!productInfo.fees?.governmentFees?.isCustomizable}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Professional Fees */}
              {showFees && !feeSettingsLoading && (
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <label className="text-md font-medium text-gray-700">
                      {productInfo.fees?.professionalFees?.name || 'Professional Fees'}
                    </label>
                    <div className="w-1/3">
                      <input
                        type="number"
                        value={data['Professional Fees'] || 0}
                        onChange={(e) => handleFeeChange(
                          key,
                          'Professional Fees',
                          null,
                          parseFloat(e.target.value) || 0
                        )}
                        className="w-full px-3 py-2 border rounded-md"
                        disabled={!productInfo.fees?.professionalFees?.isCustomizable}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Other Fees */}
              {showFees && !feeSettingsLoading && productInfo.fees?.otherFees?.map((fee, feeIndex) => (
                <div key={`${key}_${feeIndex}`} className="mb-4">
                  <div className="flex items-center justify-between">
                    <label className="text-md font-medium text-gray-700">{fee.name}</label>
                    <div className="w-1/3">
                      <input
                        type="number"
                        value={data['Other Fees']?.[fee.name] || 0}
                        onChange={(e) => handleFeeChange(
                          key,
                          'Other Fees',
                          fee.name,
                          parseFloat(e.target.value) || 0
                        )}
                        className="w-full px-3 py-2 border rounded-md"
                        disabled={!fee.isCustomizable}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Fee Information Hidden Notice */}
              {!showFees && !feeSettingsLoading && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Fee Information</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Fee information is calculated in the background but hidden from view as per system settings.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Product Required Fields */}
              {productInfo.requiredFields && productInfo.requiredFields.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-gray-700 mb-2">Required Information</h4>
                  {productInfo.requiredFields.map((field, fieldIdx) => (
                    <div key={field.label} className="mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.label}{field.isRequired && <span className="text-red-500">*</span>}
                      </label>
                      {field.fieldType === 'dropdown' ? (
                        <select
                          value={data[field.label] || ''}
                          onChange={e => handleFieldChange(parseInt(productIndex), field.label, e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                          required={field.isRequired}
                        >
                          <option value="">Select...</option>
                          {field.options && field.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : field.fieldType === 'date' ? (
                        <input
                          type="date"
                          value={data[field.label] || ''}
                          onChange={e => handleFieldChange(parseInt(productIndex), field.label, e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                          required={field.isRequired}
                        />
                      ) : field.fieldType === 'number' ? (
                        <input
                          type="number"
                          value={data[field.label] || ''}
                          onChange={e => handleFieldChange(parseInt(productIndex), field.label, e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                          required={field.isRequired}
                        />
                      ) : field.fieldType === 'email' ? (
                        <input
                          type="email"
                          value={data[field.label] || ''}
                          onChange={e => handleFieldChange(parseInt(productIndex), field.label, e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                          required={field.isRequired}
                        />
                      ) : field.fieldType === 'file' ? (
                        <PasteUploadArea
                          onFileSelect={(file) => handleFieldChange(parseInt(productIndex), field.label, file)}
                          accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          maxSize={10 * 1024 * 1024}
                          className="mt-1"
                          dragDropText="Upload a file"
                          pasteText="or paste from clipboard (Ctrl+V)"
                          disabled={uploadingFiles[`${productIndex}_${field.label}`]}
                        >
                          <DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
                          <p className="text-sm text-gray-600">Upload a file</p>
                          <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG up to 10MB</p>
                          {uploadingFiles[`${productIndex}_${field.label}`] && (
                            <div className="absolute inset-0 bg-gray-50 bg-opacity-50 flex items-center justify-center">
                              <span className="text-sm text-gray-500">Uploading...</span>
                            </div>
                          )}
                        </PasteUploadArea>
                      ) : field.fieldType === 'masterDataFile' ? (
                        <div className="mt-1">
                          {!selectedMasterData ? (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                              <p className="text-sm text-yellow-800">
                                Please select a client first to see available files.
                              </p>
                            </div>
                          ) : selectedMasterData.documents && Object.keys(selectedMasterData.documents).length > 0 ? (
                            <select
                              value={data[field.label] || ''}
                              onChange={e => {
                                const fileUrl = e.target.value;
                                handleFieldChange(parseInt(productIndex), field.label, fileUrl);
                              }}
                              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                              required={field.isRequired}
                            >
                              <option value="">Select a file...</option>
                              {Object.entries(selectedMasterData.documents).map(([key, doc]) => (
                                <option key={key} value={doc.fileUrl}>
                                  {doc.label || key} - {doc.fileName}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                              <p className="text-sm text-gray-600">
                                No files available in the selected client's master data.
                              </p>
                            </div>
                          )}
                          {data[field.label] && (
                            <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                              <p className="text-xs text-green-700">
                                Selected file URL: {data[field.label]}
                              </p>
                              <a
                                href={data[field.label]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-block"
                              >
                                View File →
                              </a>
                            </div>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={data[field.label] || ''}
                          onChange={e => handleFieldChange(parseInt(productIndex), field.label, e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                          required={field.isRequired}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Total */}
              {showFees && !feeSettingsLoading && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">Total:</span>
                    <span className="text-xl font-bold text-indigo-600">
                      ${calculateTotalFees(key)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    });
  };

  // Add this handler for ReactFormGenerator
  const handleFormChange = (data) => {
    // data is an array of {name, value}
    const newFormData = {};
    data.forEach(item => {
      newFormData[item.name] = item.value;
    });
    setFormData(newFormData);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Form Not Found</h2>
        <p className="text-gray-600">The form you're looking for doesn't exist or is not public.</p>
      </div>
    );
  }

  if (submitted) {
    const isTaskCreation = submissionType === 'task';

    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-800">
              {isTaskCreation ? 'Task Created Successfully!' : 'Thank You!'}
            </h2>
            <p className="mt-2 text-gray-600 whitespace-pre-line">{message.text}</p>
          </div>
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleSubmitAnother}
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isTaskCreation ? 'Create Another Task' : 'Submit Another Response'}
            </button>
            {onCancel && (
              <button
                onClick={onCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Return
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" style={{ maxWidth: '1000px' }}>
      {/* Master Data Selection Section */}
      <div className="mb-8 bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Select Client</h3>

        {selectedMasterData ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-blue-900">Selected Client</h4>
                <p className="text-sm text-blue-700 mt-1">{selectedMasterData.clientName}</p>
                {selectedMasterData.clientAddress && (
                  <p className="text-xs text-blue-600 mt-1">{selectedMasterData.clientAddress}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedMasterData(null)}
                className="text-blue-500 hover:text-blue-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div className="flex space-x-3 mb-3">
              <div className="flex-1 relative master-data-dropdown">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by client name, address, or contact person..."
                  value={masterDataSearchTerm}
                  onChange={(e) => setMasterDataSearchTerm(e.target.value)}
                  onFocus={() => setShowMasterDataDropdown(true)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={selectedClientType}
                onChange={(e) => setSelectedClientType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Client Types</option>
                {clientTypes.map(type => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Master Data Dropdown */}
            {showMasterDataDropdown && (
              <div className="master-data-dropdown absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {masterDataLoading ? (
                  <div className="flex justify-center items-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredMasterData.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No clients found
                  </div>
                ) : (
                  <div className="py-1">
                    {filteredMasterData.map((item) => (
                      <button
                        key={item._id}
                        onClick={() => handleMasterDataSelect(item)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{item.clientName}</div>
                            {item.clientAddress && (
                              <div className="text-sm text-gray-600">{item.clientAddress}</div>
                            )}
                          </div>
                          <CheckIcon className="h-5 w-5 text-blue-600" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Product Button */}
      {/* <div className="mb-8">
        <button
          type="button"
          onClick={handleAddProduct}
          disabled={getAvailableProductsForSelection().length === 0}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Product
        </button>
      </div> */}

      {/* Product Selection Modal */}
      <Modal isOpen={showProductModal} onClose={() => setShowProductModal(false)}>
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Select a Product</h2>
            <button
              onClick={() => setShowProductModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto">
            {getAvailableProductsForSelection().length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-lg">No additional products available</p>
                <p className="text-gray-400 text-sm mt-2">All associated products have been added to this form.</p>
              </div>
            ) : (
              getAvailableProductsForSelection().map(product => (
                <button
                  key={product._id}
                  onClick={() => handleProductSelect(product)}
                  className="text-left p-4 border rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex items-start">
                    {product.logo?.url && (
                      <img
                        src={product.logo.url}
                        alt={product.name}
                        className="w-12 h-12 object-contain mr-4"
                      />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">{product.name}</h3>
                      {product.description && (
                        <p className="mt-1 text-sm text-gray-500">{product.description}</p>
                      )}
                      <div className="mt-2 text-sm text-gray-700">
                        <p>Government Fees: ${product.fees.governmentFees.amount}</p>
                        <p>Professional Fees: ${product.fees.professionalFees.amount}</p>
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </Modal>

      {/* Form Content */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Render form designer fields using ReactFormGenerator */}
        {form && form.schema && form.schema.task_data && form.schema.task_data.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Form Information</h3>
            <ReactFormGenerator
              data={form.schema.task_data}
              answer_data={Object.entries(formData).map(([name, value]) => ({ name, value }))}
              onChange={handleFormChange}
              hide_actions={true}
            />
          </div>
        )}

        {/* Products */}
        {Object.keys(productsData).length > 0 && (
          <div className="space-y-8">
            {renderProductFees()}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {submitting ? 'Submitting...' : 'Submit Form'}
          </button>
        </div>
      </form>

      {/* Success/Error Message */}
      {showMessage && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
          {message.text}
        </div>
      )}

      {/* Submission Success View */}
      {submitted && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-gray-600 mb-6 whitespace-pre-line">{message.text}</p>
            <button
              onClick={handleSubmitAnother}
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Submit Another Form
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSubmission; 